'use client';

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useIsMobile } from '@/hooks/use-mobile';

type PrLabel = {
  id: string;
  name: string;
  color: string;
  description: string;
  isDefault: boolean;
  url: string;
};

const MAX_VISIBLE_LABELS_MOBILE = 1;
const MAX_VISIBLE_LABELS_DESKTOP = 3;
const LABEL_TOOLTIP_DELAY_MS = 100;

function normalizeLabelCandidate(value: unknown): string {
  if (typeof value === 'string') {
    return value.trim();
  }

  if (typeof value === 'number') {
    return String(value);
  }

  return '';
}

function getLabelKey(label: PrLabel, index: number): string {
  const candidates = [label.id, label.name, label.url, label.color]
    .map((value) => normalizeLabelCandidate(value))
    .filter((value) => value && value !== 'undefined' && value !== 'null');
  const base = candidates[0] ?? 'label';
  return `${base}-${index}`;
}

export function PrLabels({ labels }: { labels: PrLabel[] }) {
  const isMobile = useIsMobile();

  if (!labels.length) {
    return null;
  }

  const maxLabelsToDisplay = isMobile ? MAX_VISIBLE_LABELS_MOBILE : MAX_VISIBLE_LABELS_DESKTOP;
  const visibleLabels = labels.slice(0, maxLabelsToDisplay);
  const hiddenLabels = labels.slice(maxLabelsToDisplay);

  return (
    <div className="flex flex-wrap gap-1.5">
      {visibleLabels.map((label, index) => (
        <TooltipProvider key={getLabelKey(label, index)}>
          <Tooltip delayDuration={LABEL_TOOLTIP_DELAY_MS}>
            <TooltipTrigger asChild>
              <div className="flex cursor-default items-center gap-1.5 rounded border border-zinc-800 px-2.5 py-0.5">
                <div
                  className="size-2 rounded-full"
                  style={{ backgroundColor: `#${label.color.toUpperCase()}` }}
                />
                <span className="text-sm text-zinc-300">{label.name}</span>
              </div>
            </TooltipTrigger>
            {label.description ? <TooltipContent>{label.description}</TooltipContent> : null}
          </Tooltip>
        </TooltipProvider>
      ))}
      {hiddenLabels.length > 0 ? (
        <TooltipProvider>
          <Tooltip delayDuration={LABEL_TOOLTIP_DELAY_MS}>
            <TooltipTrigger asChild>
              <div className="flex cursor-default items-center gap-1.5 rounded border border-zinc-800 px-2.5 py-0.5">
                <span className="text-sm text-zinc-300">+{hiddenLabels.length}</span>
              </div>
            </TooltipTrigger>
            <TooltipContent>
              <div className="flex flex-col gap-1">
                {hiddenLabels.map((label, index) => (
                  <div key={getLabelKey(label, index)} className="flex items-center gap-1.5 py-0.5">
                    <div
                      className="size-2 rounded-full"
                      style={{ backgroundColor: `#${label.color.toUpperCase()}` }}
                    />
                    <span className="text-sm">{label.name}</span>
                  </div>
                ))}
              </div>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ) : null}
    </div>
  );
}
